// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useRef, useState } from "react";
import { createAccount, ready, type UnlockedKeys } from "@almanac/crypto";
import { api } from "../lib/api.js";
import { useI18n } from "../lib/i18n.js";

const MIN_LENGTH = 12;

/** First run on a new account: create the keys, then make sure the recovery key is saved. */
export function Setup({ onUnlocked }: { onUnlocked: (keys: UnlockedKeys) => void }) {
  const { t } = useI18n();
  const [passphrase, setPassphrase] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [created, setCreated] = useState<{ recoveryKey: string; keys: UnlockedKeys } | null>(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => heading.current?.focus(), [created]);

  if (created) {
    return (
      <div className="auth">
        <main className="card" id="main">
          <h1 ref={heading} tabIndex={-1}>
            {t("setup.recoveryTitle")}
          </h1>
          <p>{t("setup.recoveryIntro")}</p>
          <p className="recovery-key" aria-label={t("unlock.recoveryKey")}>
            {created.recoveryKey}
          </p>
          <div className="row" style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(created.recoveryKey);
                setCopied(true);
              }}
            >
              {copied ? t("setup.copied") : t("setup.copy")}
            </button>
            <span aria-live="polite" className="visually-hidden">
              {copied ? t("setup.copied") : ""}
            </span>
          </div>
          <label className="row" style={{ fontWeight: 450, marginBottom: 16 }}>
            <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
            {t("setup.recoveryConfirm")}
          </label>
          <button className="primary" type="button" disabled={!saved} onClick={() => onUnlocked(created.keys)}>
            {t("unlock.submit")}
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="auth">
      <main className="card" id="main">
        <h1 ref={heading} tabIndex={-1}>
          {t("setup.title")}
        </h1>
        <p>{t("setup.intro")}</p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (passphrase.length < MIN_LENGTH) return setError(t("setup.tooShort"));
            if (passphrase !== confirm) return setError(t("setup.mismatch"));
            setError(null);
            setWorking(true);
            try {
              await ready();
              // Let "Creating keys…" paint before the deliberately slow key derivation.
              await new Promise((r) => setTimeout(r, 30));
              const account = createAccount(passphrase);
              await api.putKeys(account.bundle, null);
              setCreated({ recoveryKey: account.recoveryKey, keys: account.keys });
            } catch (err) {
              setError(t("common.error", { message: (err as Error).message }));
            } finally {
              setWorking(false);
            }
          }}
        >
          <div className="field">
            <label htmlFor="new-passphrase">{t("setup.passphrase")}</label>
            <input
              id="new-passphrase"
              type="password"
              autoComplete="new-password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              aria-describedby="passphrase-hint"
              required
              minLength={MIN_LENGTH}
            />
            <p className="hint" id="passphrase-hint">
              {t("setup.hint")}
            </p>
          </div>
          <div className="field">
            <label htmlFor="confirm-passphrase">{t("setup.confirm")}</label>
            <input
              id="confirm-passphrase"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          <button className="primary" type="submit" disabled={working}>
            {working ? t("setup.working") : t("setup.create")}
          </button>
        </form>
      </main>
    </div>
  );
}
